import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/axios';
import { KeyIcon, MailIcon, PhoneIcon, AlertCircleIcon, CheckIcon, XIcon } from '../../components/Icons';

export default function OTPVerificationModal({
  identities = [],
  registrationToken = null,
  onVerificationComplete,
  onClose,
  registrationOtps = null,
}) {
  const { verifyOtp, resendOtp, loading } = useAuth();
  const [pendingIdentities, setPendingIdentities] = useState(identities);
  const [verifiedIdentities, setVerifiedIdentities] = useState([]);
  const [selectedIdentity, setSelectedIdentity] = useState(identities[0]?.value || '');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [cancelling, setCancelling] = useState(false);
  const timerInterval = useRef(null);

  useEffect(() => {
    if (pendingIdentities.length > 0 && (!selectedIdentity || !pendingIdentities.some(i => i.value === selectedIdentity))) {
      setSelectedIdentity(pendingIdentities[0]?.value || '');
    }
  }, [pendingIdentities, selectedIdentity]);

  useEffect(() => {
    return () => {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }
    };
  }, []);

  const currentIdentityObj = pendingIdentities.find((idObj) => idObj.value === selectedIdentity) || pendingIdentities[0] || null;
  const isEmail = currentIdentityObj?.type === 'email' || (selectedIdentity && selectedIdentity.includes('@'));
  const isLastPending = pendingIdentities.length <= 1;

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!otp || otp.length !== 6) {
      setError('OTP must be exactly 6 digits');
      return;
    }

    try {
      const result = await verifyOtp(registrationToken, selectedIdentity, otp);

      // Check if registration was completed (HTTP 200 with tokens)
      if (result?.access_token) {
        setSuccess('Identity verified successfully! Account is now active.');
        setTimeout(() => {
          if (onVerificationComplete) {
            onVerificationComplete(result);
          }
        }, 800);
      } else {
        // Partial verification (HTTP 202): 1 identity verified, more to go
        const remaining = result?.identities_to_verify || [];
        const verified = result?.verified_identities || [];

        setPendingIdentities(remaining);
        setVerifiedIdentities(verified);
        setOtp('');
        setSuccess(`${isEmail ? 'Email' : 'Phone'} verified! Please verify your remaining contact method.`);

        if (remaining.length > 0) {
          setSelectedIdentity(remaining[0].value);
        }
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Verification failed. Please check the OTP code and try again.');
    }
  };

  const handleCancelIdentity = async (identityValue) => {
    if (!identityValue) return;
    setError('');
    setSuccess('');
    setCancelling(true);

    try {
      const res = await api.post('/auth/cancel-identity-verification', {
        registration_token: registrationToken,
        identity_value: identityValue,
      });

      const data = res?.data;
      if (data?.access_token) {
        setSuccess('Verification cancelled. Account registration completed with verified contact.');
        setTimeout(() => {
          if (onVerificationComplete) {
            onVerificationComplete(data);
          }
        }, 800);
      } else {
        const remaining = data?.identities_to_verify || [];
        const verified = data?.verified_identities || [];
        setPendingIdentities(remaining);
        setVerifiedIdentities(verified);
        setOtp('');
        setSuccess('Verification cancelled for that contact method.');
        if (remaining.length > 0) {
          setSelectedIdentity(remaining[0].value);
        }
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to cancel verification');
    } finally {
      setCancelling(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setSuccess('');

    try {
      await resendOtp(selectedIdentity);
      setOtp('');
      setSuccess(`New ${isEmail ? 'email' : 'SMS'} verification code sent!`);
      setResendTimer(60);

      timerInterval.current = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timerInterval.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to resend OTP');
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-6 border border-slate-200 dark:border-slate-800 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
        >
          <XIcon className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2.5 rounded-xl ${
            isEmail
              ? 'bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400'
              : 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400'
          }`}>
            {isEmail ? <MailIcon className="w-6 h-6" /> : <PhoneIcon className="w-6 h-6" />}
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
              {pendingIdentities.length > 1 || verifiedIdentities.length > 0
                ? 'Verify Your Contacts'
                : isEmail
                ? 'Verify Your Email'
                : 'Verify Your Phone'}
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Enter the 6-digit code sent to your {isEmail ? 'email address' : 'phone number'}
            </p>
          </div>
        </div>

        {/* Multi-Identity Status List (when user provided both email and phone) */}
        {(pendingIdentities.length + verifiedIdentities.length > 1) && (
          <div className="mb-4 space-y-1.5 p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/60">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 block px-1">
              Verification Progress ({verifiedIdentities.length} of {verifiedIdentities.length + pendingIdentities.length} completed)
            </span>
            
            {/* Verified Identities List */}
            {verifiedIdentities.map((vid) => (
              <div key={vid.value} className="flex items-center justify-between py-1 px-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-xs">
                <div className="flex items-center gap-2 truncate">
                  <CheckIcon className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  <span className="font-mono text-[11px] text-emerald-900 dark:text-emerald-200 truncate">{vid.value}</span>
                </div>
                <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">Verified</span>
              </div>
            ))}

            {/* Pending Identities List */}
            {pendingIdentities.map((pid) => {
              const isActive = pid.value === selectedIdentity;
              return (
                <div
                  key={pid.value}
                  onClick={() => {
                    setSelectedIdentity(pid.value);
                    setOtp('');
                    setError('');
                  }}
                  className={`flex items-center justify-between py-1 px-2 rounded-lg border text-xs cursor-pointer transition-colors ${
                    isActive
                      ? 'bg-indigo-50 dark:bg-indigo-950/50 border-indigo-300 dark:border-indigo-700 text-indigo-900 dark:text-indigo-200'
                      : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-2 truncate">
                    {pid.type === 'email' ? <MailIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /> : <PhoneIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
                    <span className="font-mono text-[11px] truncate">{pid.value}</span>
                  </div>
                  <span className={`text-[10px] font-semibold ${isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400'}`}>
                    {isActive ? 'Verifying now' : 'Pending'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Prominent Current Verification Target Display */}
        <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/70 border border-slate-200/80 dark:border-slate-700/60 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`p-1.5 rounded-lg flex-shrink-0 ${
              isEmail
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
            }`}>
              {isEmail ? <MailIcon className="w-4 h-4" /> : <PhoneIcon className="w-4 h-4" />}
            </div>
            <div className="min-w-0">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-400 block">
                Verifying {isEmail ? 'Email Address' : 'Phone Number'}
              </span>
              <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 font-mono truncate block">
                {selectedIdentity || currentIdentityObj?.value}
              </span>
            </div>
          </div>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider flex-shrink-0 ${
            isEmail
              ? 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
              : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
          }`}>
            {isEmail ? 'Email' : 'SMS'}
          </span>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-400 text-xs flex items-start gap-2">
            <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400 text-xs flex items-start gap-2">
            <CheckIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{success}</span>
          </div>
        )}

        <form onSubmit={handleVerifyOtp} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 text-center">
              Enter 6-Digit Code sent to {isEmail ? 'Email' : 'Phone'}
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength="6"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="w-full text-center text-3xl font-mono tracking-widest bg-slate-50 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-700 rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              disabled={loading || cancelling}
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={loading || cancelling || otp.length !== 6}
            className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 text-white font-semibold text-xs rounded-xl shadow-md shadow-indigo-600/20 transition-all"
          >
            {loading
              ? 'Verifying OTP...'
              : isLastPending && verifiedIdentities.length > 0
              ? `Verify ${isEmail ? 'Email' : 'Phone'} & Complete Sign Up`
              : isLastPending && verifiedIdentities.length === 0
              ? `Verify ${isEmail ? 'Email' : 'Phone'} & Complete Sign Up`
              : `Verify ${isEmail ? 'Email' : 'Phone'} & Continue`}
          </button>
        </form>

        {/* Cancellation Option: If user has at least 1 verified identity, allow skipping/cancelling remaining unverified contact */}
        {verifiedIdentities.length > 0 && selectedIdentity && (
          <div className="mt-3 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700/60 text-center">
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-1.5">
              Having trouble verifying this {isEmail ? 'email' : 'phone number'}?
            </p>
            <button
              type="button"
              onClick={() => handleCancelIdentity(selectedIdentity)}
              disabled={cancelling || loading}
              className="text-xs text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 font-semibold hover:underline disabled:opacity-50 transition-colors"
            >
              {cancelling
                ? 'Cancelling...'
                : `Cancel ${isEmail ? 'Email' : 'Phone'} & Complete with ${verifiedIdentities[0]?.type === 'email' ? 'Email' : 'Phone'} only`}
            </button>
          </div>
        )}

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={handleResendOtp}
            disabled={resendTimer > 0 || loading || cancelling}
            className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold hover:underline disabled:opacity-50"
          >
            {resendTimer > 0 ? `Resend ${isEmail ? 'Email' : 'SMS'} Code in ${resendTimer}s` : `Didn't receive code? Resend ${isEmail ? 'Email' : 'SMS'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

